import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
// import { useMatomo } from '../contexts/SimpleMatomoContext';
import CapWidget from '../components/CapWidget';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { sendVerificationCode, verifyCode, isSendingCode, isVerifying } = useAuth();
  const emailLocale = (i18n.language || 'en').split('-')[0];
  // const { trackCustomEvent, trackUserLogin, trackUserRegistration } = useMatomo();
  const trackCustomEvent = () => {}; // Mock function
  const trackUserLogin = () => {}; // Mock function
  
  const [step, setStep] = useState('email'); // 'email' or 'verify'
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTime, setBlockTime] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);

  const pageTitle =
    role === 'land_reporter'
      ? t('auth.landReporter')
      : role === 'land_seeker'
        ? t('auth.landSeeker')
        : t('navigation.admin');

  // Get role from navigation state and auto-set it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roleParam = params.get('role');
    const validRoles = ['land_reporter', 'land_seeker', 'admin'];

    if (location.state?.role) {
      setRole(location.state.role);
      return;
    }

    if (roleParam && validRoles.includes(roleParam)) {
      setRole(roleParam);
    } else {
      navigate('/role-selection', { replace: true });
    }
  }, [location.state, location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    const codeParam = params.get('code');
    const roleParam = params.get('role');
    const validRoles = ['land_reporter', 'land_seeker', 'admin'];

    if (emailParam) {
      setEmail(emailParam);
    }

    if (roleParam && validRoles.includes(roleParam)) {
      setRole(roleParam);
    }

    if (emailParam && codeParam) {
      const sanitizedCode = codeParam.replace(/\D/g, '').slice(0, 6);
      if (sanitizedCode.length === 6) {
        setVerificationCode(sanitizedCode);
        setStep('verify');
      }
    }
  }, [location.search]);

  // Check if user is blocked
  useEffect(() => {
    const checkBlockStatus = () => {
      const lastAttempt = localStorage.getItem('lastVerificationAttempt');
      const attemptCount = localStorage.getItem('verificationAttempts') || 0;
      
      if (lastAttempt) {
        const timeDiff = Date.now() - parseInt(lastAttempt);
        const tenMinutes = 10 * 60 * 1000;
        
        if (timeDiff < tenMinutes && parseInt(attemptCount) >= 3) {
          setIsBlocked(true);
          setBlockTime(Math.ceil((tenMinutes - timeDiff) / 1000));
        }
      }
    };
    
    checkBlockStatus();
  }, []);

  // Update block timer
  useEffect(() => {
    if (isBlocked && blockTime > 0) {
      const timer = setTimeout(() => {
        setBlockTime(blockTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (blockTime === 0) {
      setIsBlocked(false);
      localStorage.removeItem('verificationAttempts');
      localStorage.removeItem('lastVerificationAttempt');
    }
  }, [isBlocked, blockTime]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Note: Captcha verification is temporarily disabled on backend
    // if (!captchaToken) {
    //   toast.error('Please complete the captcha');
    //   return;
    // }

    if (!email) {
      toast.error(t('auth.enterEmail'));
      return;
    }

    if (!role) {
      toast.error(t('auth.selectRole'));
      return;
    }

    if (!captchaToken) {
      toast.error(t('loginPage.securityVerificationRequired'));
      return;
    }

    try {
      console.log('Sending verification code for:', { email, role });
      const requestData = { email, role, captcha: captchaToken, locale: emailLocale };
      await sendVerificationCode(requestData);
      console.log('Code sent successfully, switching to verify step');
      setStep('verify');
      toast.success(t('auth.codeSent'));
      
      // Record attempt
      const attemptCount = parseInt(localStorage.getItem('verificationAttempts') || 0) + 1;
      localStorage.setItem('verificationAttempts', attemptCount.toString());
      localStorage.setItem('lastVerificationAttempt', Date.now().toString());
      
    } catch (error) {
      console.error('Error sending verification code:', error);
      if (error.response?.data?.current_role && error.response?.data?.attempted_role) {
        const currentRole = error.response.data.current_role.replace(/_/g, ' ');
        const attemptedRole = error.response.data.attempted_role.replace(/_/g, ' ');
        toast.error(t('loginPage.roleMismatch', { currentRole, attemptedRole }));
      } else {
        toast.error(error.message || t('loginPage.failedToSendCode'));
      }
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t('auth.enterValidCode'));
      return;
    }

    try {
      console.log('Verifying code for:', { email, verificationCode });
      await verifyCode({ email, verificationCode });
      toast.success(t('auth.loginSuccess'));
      setIsNavigating(true);
      
      // Track successful login
      trackCustomEvent('Authentication', 'Login Success', role);
      
      if (rememberMe) {
        localStorage.setItem('rememberEmail', email);
      }
      
      // Add a small delay to ensure token is set before navigation
      setTimeout(async () => {
        // Navigate based on role
        if (role === 'land_reporter') {
          try {
            const token = localStorage.getItem('token');
            const resp = await fetch(apiUrl('/auth/me'), {
              headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
            });
            if (resp.ok) {
              const me = await resp.json();
              if (me?.data?.needs_profile_completion) {
                navigate('/onboarding/reporter');
                return;
              }
            }
          } catch {}
          trackCustomEvent('Navigation', 'Login Redirect', 'report');
          navigate('/report');
        } else if (role === 'land_seeker') {
          // Check if user has active subscription
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/auth/me'), {
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (response.ok) {
              const userData = await response.json();
              console.log('Login flow - User data:', userData.data);
              console.log('Login flow - Has active subscription:', userData.data?.has_active_subscription);
              
              // Track user login with full user data
              trackUserLogin(userData.data);
              
              if (userData.data?.has_active_subscription) {
                console.log('Login flow - Redirecting to /search');
                trackCustomEvent('Navigation', 'Login Redirect', 'search');
                navigate('/search'); // Users with subscription go to search page
              } else {
                console.log('Login flow - Redirecting to /preview');
                trackCustomEvent('Navigation', 'Login Redirect', 'preview');
                navigate('/preview'); // Users without subscription go to preview page
              }
            } else {
              console.log('Login flow - Response not ok, redirecting to /preview');
              navigate('/preview'); // Fallback to preview page
            }
          } catch (error) {
            console.error('Error checking subscription status:', error);
            navigate('/preview'); // Fallback to preview page
          }
        } else {
          navigate('/');
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error verifying code:', error);
      if (error.response?.data?.current_role && error.response?.data?.attempted_role) {
        const currentRole = error.response.data.current_role.replace(/_/g, ' ');
        toast.error(t('loginPage.roleChangeNotAllowed', { currentRole }));
      } else {
        toast.error(error.message || t('auth.invalidCode'));
      }
      setAttempts(attempts + 1);
      
      if (attempts >= 2) {
        setIsBlocked(true);
        setBlockTime(600); // 10 minutes
        localStorage.setItem('verificationAttempts', '3');
        localStorage.setItem('lastVerificationAttempt', Date.now().toString());
      }
    }
  };

  const handleResendCode = async () => {
    // Note: Captcha verification is temporarily disabled on backend
    // if (!captchaToken) {
    //   toast.error('Please complete the captcha');
    //   return;
    // }

    try {
      if (!captchaToken) {
        toast.error(t('auth.completeCaptcha'));
        setStep('email');
        return;
      }
      // Only send captcha if it exists (when captcha is enabled)
      const requestData = { email, role, captcha: captchaToken, locale: emailLocale };
      await sendVerificationCode(requestData);
      toast.success(t('loginPage.newCodeSent'));
    } catch (error) {
      toast.error(error.message || t('loginPage.failedToResend'));
    }
  };

  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isBlocked) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('loginPage.tooManyAttemptsTitle')}</h2>
            <p className="text-gray-600 mb-4">
              {t('loginPage.tooManyAttemptsBody')}
            </p>
            <p className="text-lg font-semibold text-red-600">
              {t('loginPage.tryAgainIn', { time: formatTime(blockTime) })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{pageTitle}</h1>
          <p className="text-gray-600">
            {step === 'email' 
              ? (role === 'land_reporter'
                  ? t('loginPage.emailStepSubtitleReporter')
                  : t('loginPage.emailStepSubtitleSeeker'))
              : t('loginPage.verifyStepSubtitle')
            }
          </p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('loginPage.emailPlaceholder')}
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="remember" className="text-sm text-gray-600">
                {t('loginPage.rememberMe')}
              </label>
            </div>

            <div className="flex justify-center">
              <CapWidget
                siteKey="salesign-demo"
                onVerify={onCaptchaChange}
                onError={(error) => {
                  console.error('Cap verification failed:', error);
                  setCaptchaToken(null);
                }}
                theme="light"
                size="normal"
              />
            </div>

            <button
              type="submit"
              disabled={isSendingCode}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSendingCode ? t('loginPage.sending') : t('loginPage.sendVerificationCode')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="text-center mb-4">
              {isNavigating ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-blue-600 font-medium">{t('loginPage.redirecting')}</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600">
                    {t('loginPage.sentCodeTo')} <strong>{email}</strong>
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('loginPage.codeExpires')}
                  </p>
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.verificationCode')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                placeholder="123456"
                maxLength="6"
                required
              />
            </div>

            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="flex-1 btn-outline py-3"
              >
                {t('common.back')}
              </button>
              <button
                type="submit"
                disabled={isVerifying || verificationCode.length !== 6 || isNavigating}
                className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isNavigating ? t('loginPage.redirecting') : isVerifying ? t('loginPage.verifying') : t('loginPage.verifyAndLogin')}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                {t('loginPage.resend')}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← {t('loginPage.backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;